import React, { useState, useRef } from "react";
import { motion } from "framer-motion";
import { Upload, FileText, X, ShieldCheck, CheckCircle2, Loader2 } from "lucide-react";
import { useLang } from "@/i18n";
import { api } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";

export const QuoteForm = () => {
  const { t, lang } = useLang();
  const q = t.quote;
  const fileRef = useRef(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", sector: "", formula: "", vehicle: "", message: "" });
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [gdpr, setGdpr] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onFiles = async (e) => {
    const selected = Array.from(e.target.files || []);
    if (!selected.length) return;
    setUploading(true);
    for (const file of selected) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setFiles((f) => [...f, { id: data.file_id, name: data.filename }]);
      } catch (err) {
        toast.error(err.response?.data?.detail || "Erreur upload");
      }
    }
    setUploading(false);
  };

  const removeFile = (id) => setFiles((f) => f.filter((x) => x.id !== id));

  const submit = async (e) => {
    e.preventDefault();
    if (!gdpr) { toast.error("Veuillez accepter les conditions RGPD."); return; }
    if (!form.name || !form.email || !form.phone || !form.sector || !form.formula) { toast.error("Veuillez remplir les champs obligatoires."); return; }
    setSubmitting(true);
    try {
      await api.post("/quotes", { ...form, gdpr_consent: gdpr, file_ids: files.map((f) => f.id), lang });
      setDone(true);
      toast.success(q.success);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally { setSubmitting(false); }
  };

  const sectors = ["Automobile", "Camion / Transport", "Génie civil", "Autre"];
  const formulas = ["Leasing", "Renting", "LLD", "Crédit classique"];

  if (done) {
    return (
      <section id="quote" className="py-24 sm:py-32 max-w-2xl mx-auto px-5 sm:px-8" data-testid="quote-success">
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
          className="glow-border glass rounded-2xl p-10 text-center accent-glow">
          <CheckCircle2 className="w-16 h-16 text-accent mx-auto mb-5" strokeWidth={1.5} />
          <h3 className="font-display text-2xl font-bold">{q.success}</h3>
          <p className="mt-3 text-muted-foreground">i.lease.be@gmail.com · 067/87.05.53</p>
        </motion.div>
      </section>
    );
  }

  return (
    <section id="quote" className="relative py-24 sm:py-32 max-w-3xl mx-auto px-5 sm:px-8" data-testid="quote-form">
      <div className="mb-10 text-center">
        <h2 className="font-display text-3xl sm:text-5xl font-bold">{q.title}</h2>
        <p className="mt-4 text-muted-foreground text-lg">{q.subtitle}</p>
      </div>

      <form onSubmit={submit} className="glow-border glass rounded-2xl p-6 sm:p-8 space-y-5">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label={q.name + " *"}><Input value={form.name} onChange={set("name")} data-testid="quote-name" className="bg-white/5 border-white/10 focus-visible:ring-accent" /></Field>
          <Field label={q.email + " *"}><Input type="email" value={form.email} onChange={set("email")} data-testid="quote-email" className="bg-white/5 border-white/10 focus-visible:ring-accent" /></Field>
          <Field label={q.phone + " *"}><Input value={form.phone} onChange={set("phone")} data-testid="quote-phone" className="bg-white/5 border-white/10 focus-visible:ring-accent" /></Field>
          <Field label={q.company}><Input value={form.company} onChange={set("company")} data-testid="quote-company" className="bg-white/5 border-white/10 focus-visible:ring-accent" /></Field>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <Field label={q.sector + " *"}>
            <select value={form.sector} onChange={set("sector")} data-testid="quote-sector"
              className="w-full h-10 rounded-md bg-white/5 border border-white/10 px-3 text-sm outline-none focus:border-accent">
              <option value="" className="bg-[#0d2138]">{q.selectSector}</option>
              {sectors.map((s) => <option key={s} value={s} className="bg-[#0d2138]">{s}</option>)}
            </select>
          </Field>
          <Field label={q.formula + " *"}>
            <select value={form.formula} onChange={set("formula")} data-testid="quote-formula"
              className="w-full h-10 rounded-md bg-white/5 border border-white/10 px-3 text-sm outline-none focus:border-accent">
              <option value="" className="bg-[#0d2138]">{q.selectFormula}</option>
              {formulas.map((s) => <option key={s} value={s} className="bg-[#0d2138]">{s}</option>)}
            </select>
          </Field>
        </div>

        <Field label={q.vehicle}><Input value={form.vehicle} onChange={set("vehicle")} placeholder={q.vehiclePh} data-testid="quote-vehicle" className="bg-white/5 border-white/10 focus-visible:ring-accent" /></Field>
        <Field label={q.message}><Textarea value={form.message} onChange={set("message")} rows={3} data-testid="quote-message" className="bg-white/5 border-white/10 focus-visible:ring-accent" /></Field>

        <div>
          <label className="text-sm text-muted-foreground">{q.docs}</label>
          <div onClick={() => fileRef.current?.click()} data-testid="quote-upload-zone"
            className="mt-2 cursor-pointer rounded-xl border border-dashed border-white/15 hover:border-accent/50 transition-colors p-6 text-center">
            {uploading ? <Loader2 className="w-6 h-6 mx-auto text-accent animate-spin" /> : <Upload className="w-6 h-6 mx-auto text-accent" strokeWidth={1.5} />}
            <p className="mt-2 text-sm text-muted-foreground">{uploading ? q.uploading : q.upload}</p>
          </div>
          <input ref={fileRef} type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={onFiles} className="hidden" data-testid="quote-file-input" />
          {files.length > 0 && (
            <div className="mt-3 space-y-2">
              {files.map((f) => (
                <div key={f.id} className="flex items-center justify-between rounded-lg bg-white/5 px-3 py-2 text-sm" data-testid={`quote-file-${f.id}`}>
                  <span className="flex items-center gap-2 truncate"><FileText className="w-4 h-4 text-accent shrink-0" strokeWidth={1.5} /> {f.name}</span>
                  <button type="button" onClick={() => removeFile(f.id)} className="text-muted-foreground hover:text-red-400"><X className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          )}
        </div>

        <label className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/[0.02] p-4 cursor-pointer" data-testid="quote-gdpr-label">
          <Checkbox checked={gdpr} onCheckedChange={setGdpr} data-testid="quote-gdpr" className="mt-0.5 border-white/30 data-[state=checked]:bg-accent data-[state=checked]:border-accent" />
          <span className="text-xs text-muted-foreground leading-relaxed flex items-start gap-2">
            <ShieldCheck className="w-4 h-4 text-accent shrink-0 mt-0.5" strokeWidth={1.5} /> {q.gdpr}
          </span>
        </label>

        <p className="text-center text-[14px] font-bold text-amber-300 uppercase tracking-wide" data-testid="quote-warning">
          {t.footer.warningBig}
        </p>

        <Button type="submit" disabled={submitting} data-testid="quote-submit"
          className="w-full rounded-full bg-white text-black hover:bg-accent hover:text-white transition-colors h-12 font-medium">
          {submitting ? q.submitting : q.submit}
        </Button>
      </form>
    </section>
  );
};

const Field = ({ label, children }) => (
  <div>
    <label className="text-sm text-muted-foreground mb-2 block">{label}</label>
    {children}
  </div>
);
